import { useState, type FormEvent } from "react"
import {
  ArrowRight,
  Building07,
  Car01,
  CheckCircle,
  LayersThree01,
  Tool01,
} from "@untitledui/icons"
import { AnimatePresence, motion } from "motion/react"
import type { TeamType } from "@/lib/api/types"
import { useOnboarding } from "@/lib/onboarding/onboarding-context"
import { companyIdentitySchema } from "@/lib/onboarding/schemas"
import {
  Field,
  PrimaryButton,
  StaggerField,
  StepActions,
  StepLayout,
  onboardingInputClass,
  onboardingInputErrorClass,
} from "./onboarding-shell"
import { OnboardingContextPanel } from "./context-panel"
import { cx } from "@/lib/utils/cx"

// Step 2a — Identity. Three decisions on one focused panel: org type, role
// for that type, business name. Address moves to step 2b. The team is NOT
// created here — we just persist into the onboarding context's companyDraft
// and the actual API call lives at the end of step 2b. That keeps the
// happy-path team-creation in a single network round-trip and prevents
// orphan team rows if a user abandons mid-flow.
//
// "Settle" interaction on the type tiles: as soon as a type is picked the
// other two fade to 50% (visibly still clickable for change-of-mind) and
// the active tile scales 1.04. The role picker reveals beneath via opacity
// + y with an 80ms delay so it reads as a second beat, not a flash. Net
// effect: the page feels like it's responding to the user, not just
// scrolling.

const TYPE_OPTIONS: {
  value: TeamType
  label: string
  description: string
  icon: typeof Building07
  tone: { from: string; to: string; ring: string; bg: string; text: string }
}[] = [
  {
    value: "Dealership",
    label: "Dealership",
    description: "New & used auto sales with service",
    icon: Car01,
    tone: {
      from: "from-sky-500",
      to: "to-sky-700",
      ring: "ring-sky-500/20",
      bg: "bg-sky-50",
      text: "text-sky-700",
    },
  },
  {
    value: "Bodyshop",
    label: "Body Shop",
    description: "Collision repair & paint",
    icon: Tool01,
    tone: {
      from: "from-amber-500",
      to: "to-amber-700",
      ring: "ring-amber-500/20",
      bg: "bg-amber-50",
      text: "text-amber-700",
    },
  },
  {
    value: "DealershipGroup",
    label: "Group",
    description: "Multiple dealership locations",
    icon: LayersThree01,
    tone: {
      from: "from-indigo-500",
      to: "to-indigo-700",
      ring: "ring-indigo-500/20",
      bg: "bg-indigo-50",
      text: "text-indigo-700",
    },
  },
]

// Mirrors `BACKEND_ROLES_BY_TEAM_TYPE` in invite-members-step + team-settings.
// First role is the default applied when a type is picked (matches the
// usersApi.getMe team-type fallback).
const ROLES_BY_TYPE: Record<TeamType, string[]> = {
  DealershipGroup: ["Director", "General Manager"],
  Dealership: [
    "General Manager",
    "Service Manager",
    "Service Advisor",
    "Sales Manager",
    "Sales Representative",
  ],
  Bodyshop: ["General Manager", "Estimator"],
}

interface FieldErrors {
  type?: string
  role?: string
  name?: string
}

export function CompanyIdentityStep() {
  const {
    goBack,
    goNext,
    selectedType,
    setSelectedType,
    selectedRole,
    setSelectedRole,
    companyDraft,
    setCompanyDraft,
    setCompanySnapshot,
  } = useOnboarding()

  // Step-local form state seeded from context so back-nav from step 2b
  // returns the user to exactly what they had. Keep this state hands-off:
  // every change writes through to the context draft AND the panel snapshot
  // so the right-column panel updates in real time.
  const [type, setTypeState] = useState<TeamType | "">(selectedType ?? companyDraft.type ?? "")
  const [role, setRoleState] = useState<string>(selectedRole ?? "")
  const [name, setNameState] = useState<string>(companyDraft.name ?? "")
  const [errors, setErrors] = useState<FieldErrors>({})

  const updateType = (value: TeamType) => {
    setTypeState(value)
    setSelectedType(value)
    setCompanyDraft({ type: value })
    // Default role for the type if current role isn't valid for it.
    const validRoles = ROLES_BY_TYPE[value]
    const nextRole = validRoles.includes(role) ? role : validRoles[0]
    if (nextRole !== role) {
      setRoleState(nextRole)
      setSelectedRole(nextRole)
    }
    if (errors.type) setErrors((e) => ({ ...e, type: undefined }))
  }

  const updateRole = (value: string) => {
    setRoleState(value)
    setSelectedRole(value)
    if (errors.role) setErrors((e) => ({ ...e, role: undefined }))
  }

  const updateName = (value: string) => {
    setNameState(value)
    setCompanyDraft({ name: value })
    setCompanySnapshot({
      name: value,
      city: companyDraft.city,
      state: companyDraft.state,
    })
    if (errors.name) setErrors((e) => ({ ...e, name: undefined }))
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    const fieldErrors: FieldErrors = {}
    const parsed = companyIdentitySchema.safeParse({ type, name })
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const key = issue.path[0]
        if (key === "type" || key === "name") {
          if (!fieldErrors[key]) fieldErrors[key] = issue.message
        }
      }
    }
    if (!type) fieldErrors.type = fieldErrors.type ?? "Pick your organization type"
    if (type && !ROLES_BY_TYPE[type as TeamType].includes(role)) {
      fieldErrors.role = "Pick the role you'll have on this team"
    }
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors)
      return
    }
    setErrors({})
    goNext()
  }

  const validRoles = type ? ROLES_BY_TYPE[type] : []

  return (
    <StepLayout
      eyebrow="Step 2 — Company"
      title="Tell us about your business"
      subtitle="The basics — what kind of operation, your role on the team, and the name customers see."
      hero={<OnboardingContextPanel />}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Org-type tiles with settle interaction. */}
        <StaggerField index={0}>
          <Field label="Organization type" error={errors.type} required>
            <div className="grid grid-cols-3 gap-3">
              {TYPE_OPTIONS.map((opt) => {
                const active = type === opt.value
                const dimmed = type !== "" && !active
                return (
                  <motion.button
                    key={opt.value}
                    type="button"
                    onClick={() => updateType(opt.value)}
                    whileTap={{ scale: 0.97 }}
                    animate={{
                      scale: active ? 1.04 : 1,
                      opacity: dimmed ? 0.5 : 1,
                    }}
                    transition={{ type: "spring", stiffness: 300, damping: 24 }}
                    className={cx(
                      "group relative flex flex-col items-center gap-2.5 p-4 rounded-2xl border-2 text-center transition-colors duration-200",
                      active
                        ? `border-zinc-900 bg-zinc-50 shadow-md shadow-zinc-900/[0.06]`
                        : "border-zinc-200 bg-white hover:border-zinc-300 hover:shadow-sm",
                    )}
                  >
                    {active && (
                      <motion.span
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 500, damping: 25 }}
                        className="absolute top-2 right-2"
                      >
                        <CheckCircle className="size-[18px] text-emerald-600" aria-hidden />
                      </motion.span>
                    )}
                    <div
                      className={cx(
                        "size-12 rounded-2xl grid place-items-center transition-all duration-200",
                        active
                          ? `bg-gradient-to-br ${opt.tone.from} ${opt.tone.to} shadow-lg ${opt.tone.ring} ring-4`
                          : `${opt.tone.bg}`,
                      )}
                    >
                      <opt.icon
                        className={cx(
                          "size-6 transition-colors duration-200",
                          active ? "text-white" : opt.tone.text,
                        )}
                        strokeWidth={2}
                        aria-hidden
                      />
                    </div>
                    <div className="space-y-0.5">
                      <div
                        className={cx(
                          "text-[14px] font-semibold",
                          active ? "text-zinc-900" : "text-zinc-700",
                        )}
                      >
                        {opt.label}
                      </div>
                      <div className="text-[11px] leading-snug text-zinc-400">
                        {opt.description}
                      </div>
                    </div>
                  </motion.button>
                )
              })}
            </div>
          </Field>
        </StaggerField>

        {/* Role picker — opacity+y reveal with an 80ms delay so it lands as a
            second beat after the tile pick. AnimatePresence keeps it out of
            the DOM tab order until the user has chosen a type. */}
        <AnimatePresence initial={false}>
          {type && (
            <motion.div
              key="role"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0, transition: { delay: 0.08, duration: 0.3, ease: [0.16, 1, 0.3, 1] } }}
              exit={{ opacity: 0, y: -4, transition: { duration: 0.15 } }}
            >
              <Field
                label="Your role on this team"
                error={errors.role}
                hint="What you do day-to-day. Drives navigation, dashboards, and what you can do in the app."
                required
              >
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {validRoles.map((roleName) => {
                    const active = role === roleName
                    return (
                      <button
                        key={roleName}
                        type="button"
                        onClick={() => updateRole(roleName)}
                        className={cx(
                          "h-11 rounded-xl border-2 px-3 text-[13px] font-semibold transition-all duration-150",
                          active
                            ? "border-emerald-500 bg-emerald-50 text-emerald-900 shadow-sm shadow-emerald-600/10"
                            : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300 hover:bg-zinc-50",
                        )}
                      >
                        {roleName}
                      </button>
                    )
                  })}
                </div>
              </Field>
            </motion.div>
          )}
        </AnimatePresence>

        <StaggerField index={2}>
          <Field label="Business name" error={errors.name} required>
            <input
              value={name}
              onChange={(e) => updateName(e.target.value)}
              maxLength={255}
              placeholder={
                type === "Bodyshop"
                  ? "Precision Collision Center"
                  : type === "DealershipGroup"
                    ? "AutoNation Southeast"
                    : "Austin Honda"
              }
              className={cx(onboardingInputClass, errors.name && onboardingInputErrorClass)}
            />
          </Field>
        </StaggerField>

        <StaggerField index={3}>
          <StepActions
            onBack={goBack}
            primary={
              <PrimaryButton variant="emerald">
                Continue
                <ArrowRight className="size-[18px]" aria-hidden />
              </PrimaryButton>
            }
          />
        </StaggerField>
      </form>
    </StepLayout>
  )
}
